import { AzavistaApiService } from '@azavista/servicelib';
import { objectKeys, PickByType } from '@azavista/workflow-builder/shared';
import { HostContextEntity } from './azavista-ai-chatbot.types';
import  ObjectID from 'bson-objectid';

const VIEW_NAVIGATOR_SKIPPED_TERMS = ['create', 'list', 'archive', 'event.list', 'event.archive', 'event-template.list'];

type ApiServiceMethod = keyof PickByType<AzavistaApiService, Function>;

type ViewNavigatorServiceToHostContextEntity<T extends ApiServiceMethod> = {
    modules: string[];
    entityGetter: (data: Awaited<ReturnType<AzavistaApiService[T]>>) => Omit<HostContextEntity, 'module'>;
}

export const VIEW_NAVIGATOR_ENTITY_SERVICELIB_METHODS = {
    getEvent: {
        modules: ['event'] as const,
        entityGetter: (data) => ({ id: data.id, name: data.name,  }),
    },
    getEventParticipant: {
        modules: ['participant'] as const,
        entityGetter: (data) => ({ id: data.id, name: `${data.first_name} ${data.last_name}`.trim(),  }),
    },
    getEventActivity: {
        modules: ['activity', 'session'] as const,
        entityGetter: (data) => ({ id: data.id, name: data.name,  }),
    },
    getContact: {
        modules: ['contact'] as const,
        entityGetter: (data) => ({ id: data.id, name: `${data.first_name} ${data.last_name}`.trim(),  }),
    },
    getOrganization: {
        modules: ['organization'] as const,
        entityGetter: (data) => ({ id: data.id, name: data.name,  }),
    },
    getProfilePage: {
        modules: ['profile-page'] as const,
        entityGetter: (data) => ({ id: data.id, name: data.name,  }),
    },
    getUser: {
        modules: ['user'] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: `${data.first_name} ${data.last_name}`.trim(),  }),
    },
    getTeam: {
        modules: ['team'] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getRole: {
        modules: ['role'] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getApiClient: {
        modules: ['api-client'] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getIntegration: {
        modules: ['integration'] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getEmailCampaign: {
        modules: [
            'email-template',
            'email-marketing'
            // TODO: double-check module-name for path `contacts-email-marketing/email-campaigns/details?emailCampaignId=5ece5fa462fbaf0011259a5c`
        ] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getProcess: {
        modules: [`event-process`, `participant-process`, `contact-process`, `organization-process`] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getGlobalReport: {
        modules: ['report'] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getProduct: {
        modules: ['ticket'] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getTheme: {
        modules: ['theme'] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getEventApp: {
        modules: [`app`] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getLocation: {
        modules: [`location`] as const,
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getFloorPlan: {
        modules: [`floor-plan`] as const, // TODO: double-check existence
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getRoom: {
        modules: [`room`] as const, // TODO: double-check existence
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getAmenity: {
        modules: [`amenity`] as const, // TODO: double-check existence
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getEngagement: {
        modules: [`engagement`] as const, // TODO: double-check existence
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getEventBadge: {
        modules: [`badge`] as const, // TODO: double-check existence
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getProject: {
        modules: ['project'] as const,  // TODO: double-check existence
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },
    getProjectTask: {
        modules: [`task`] as const, // TODO: double-check existence
        entityGetter: (data) => ({ id: `${data.id}`, name: data.name,  }),
    },

} satisfies Partial<{
    [methodName in ApiServiceMethod]: ViewNavigatorServiceToHostContextEntity<methodName>;
}>;

export type ViewNavigatorEntityServiceLibMethodRecord = typeof VIEW_NAVIGATOR_ENTITY_SERVICELIB_METHODS;
export type ViewNavigatorEntityServiceLibMethodRecordKey = keyof ViewNavigatorEntityServiceLibMethodRecord;
export type ViewNavigatorEntityServiceLibMethodsRecordItem = ViewNavigatorEntityServiceLibMethodRecord[ViewNavigatorEntityServiceLibMethodRecordKey];
export type ViewNavigatorEntityModuleName = ViewNavigatorEntityServiceLibMethodsRecordItem['modules'][number];

const VIEW_NAVIGATOR_ENTITY_GETTERS = objectKeys(VIEW_NAVIGATOR_ENTITY_SERVICELIB_METHODS).reduce((record, methodName) => {
    const item = VIEW_NAVIGATOR_ENTITY_SERVICELIB_METHODS[methodName];
    for (const moduleName of item.modules) {
        record[moduleName] = item;
    }
    return record;
}, {} as {
    [moduleName in ViewNavigatorEntityModuleName]: ViewNavigatorEntityServiceLibMethodsRecordItem
});

function isStrictNumber(str: string) {
  // Matches optional negative sign, at least one digit, and optional decimal part
  return /^-?\d+(\.\d+)?$/.test(str);
}

export const getViewEntityIdPairs = (view: string) => {
    // split by `/` or `.`
    const viewSplit = view.split(/[./]/);
    // get consecutive pairs of module name and entity id,
    // if viewSplit's item is number or bson, treat it as an entity id
    return viewSplit.reduce((acc, item, index) => {
        if (index > 0) {
            if (isStrictNumber(item) || ObjectID.isValid(item)) {
                acc.push({ module: viewSplit[index - 1], id: item });
            }
        }
        return acc;
    }, [] as Array<{ module: string, id: string }>);

};

/**
 * Extracts the hash fragment modules and IDs from a URL string.
 * @param url
 * @returns
 */
export function getHashFragmentModules(url: string) {
    const hashIndex = url.indexOf('#');
    const hashString = hashIndex >= 0 ? url.slice(hashIndex + 1) : '';
    const elementPairs = hashString.split('/').map(pair => {
        const [module, id] = pair.split('.');
        return { module, id, fullId: pair };
    });

    return elementPairs;
}
